import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Section, PrimaryButton } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { ArrowLeft } from "lucide-react";

// Minimal Markdown -> HTML (h2/h3/lists/bold/p). Avoids heavy deps.
function renderMarkdown(md) {
    if (!md) return "";
    const lines = md.split(/\r?\n/);
    let html = "";
    let inUl = false;
    let inOl = false;
    const closeLists = () => {
        if (inUl) { html += "</ul>"; inUl = false; }
        if (inOl) { html += "</ol>"; inOl = false; }
    };
    const inline = (s) =>
        s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
         .replace(/\*(.+?)\*/g, "<em>$1</em>")
         .replace(/`([^`]+)`/g, "<code>$1</code>");
    for (const raw of lines) {
        const line = raw.trim();
        if (!line) { closeLists(); continue; }
        if (line.startsWith("## ")) { closeLists(); html += `<h2>${inline(line.slice(3))}</h2>`; continue; }
        if (line.startsWith("### ")) { closeLists(); html += `<h3>${inline(line.slice(4))}</h3>`; continue; }
        if (line.startsWith("# ")) { closeLists(); html += `<h2>${inline(line.slice(2))}</h2>`; continue; }
        if (/^[-*]\s+/.test(line)) {
            if (!inUl) { closeLists(); html += "<ul>"; inUl = true; }
            html += `<li>${inline(line.replace(/^[-*]\s+/, ""))}</li>`;
            continue;
        }
        if (/^\d+\.\s+/.test(line)) {
            if (!inOl) { closeLists(); html += "<ol>"; inOl = true; }
            html += `<li>${inline(line.replace(/^\d+\.\s+/, ""))}</li>`;
            continue;
        }
        closeLists();
        html += `<p>${inline(line)}</p>`;
    }
    closeLists();
    return html;
}

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        setPost(null);
        setErr("");
        api.get(`/blog/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setErr("Artykuł nie został jeszcze opublikowany."));
    }, [slug]);

    if (err) {
        return (
            <Section variant="light" className="pt-32">
                <div className="max-w-2xl mx-auto text-center">
                    <h1 className="font-display font-bold text-3xl mb-4 text-[#0B1220]">Artykuł niedostępny</h1>
                    <p className="text-[#0B1220]/70 mb-6">{err}</p>
                    <PrimaryButton to="/blog" testid="blog-back">← Wróć do bloga</PrimaryButton>
                </div>
            </Section>
        );
    }
    if (!post) return <Section variant="light" className="pt-32"><div className="text-center text-[#0B1220]/50">Wczytywanie…</div></Section>;

    return (
        <>
            <SEO title={`${post.title} – Szymkas Agency`} description={post.excerpt} />
            <section className="bg-[#06111F] text-white pt-32 pb-16 md:pt-40 md:pb-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-faint opacity-50" />
                <div className="relative max-w-3xl mx-auto px-5 md:px-8">
                    <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-[#AAB6C5] hover:text-[#14B8A6] mb-4" data-testid="blog-detail-back">
                        <ArrowLeft className="w-4 h-4" /> Wszystkie artykuły
                    </Link>
                    <div className="text-xs font-semibold tracking-widest uppercase text-[#14B8A6] mb-3">{post.category}</div>
                    <h1 className="font-display font-extrabold text-[32px] md:text-[44px] lg:text-[52px] leading-tight tracking-tight">{post.title}</h1>
                    {post.excerpt && <p className="mt-5 text-[#AAB6C5] text-base md:text-lg leading-relaxed">{post.excerpt}</p>}
                </div>
            </section>
            <Section variant="light">
                <article className="prose-article max-w-3xl mx-auto" data-testid="blog-detail-content" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }} />
                <div className="max-w-3xl mx-auto mt-12 pt-8 border-t border-[#E5E7EB] text-center">
                    <p className="text-[#0B1220]/70 mb-4">Potrzebujesz wsparcia dla swojej firmy?</p>
                    <PrimaryButton to="/kontakt" testid="blog-detail-cta">Umów bezpłatną konsultację</PrimaryButton>
                </div>
            </Section>
        </>
    );
}
